import { CustomCellEditorProps } from "ag-grid-react";
import { Select } from "antd";
import DesignReviewerDropdown from "components/design-reviwer-dropdown";
import GCReviewersDropdown from "components/gc-reveiewer-dropdown";
import ResponsibleDesignFirmsDropdown from "components/responsible-design-firms";
import DependedCompanyUserDropdownComponent from "components/responsible-designers";
import { ProjectContext } from "context/ProjectProvider";
import { getValueFromDotPath, setNestedValue } from "entity-app/utils/utils";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  ProjectParticipants,
  useProjectParticipants
} from "hooks/project-participants";
import { useMemo, useContext, useState, useEffect } from "react";
import { GET_PROJECT_WORKFLOW_TEMPLATE_NAMES } from "services/graphQL/ciq-queries";

function WorkflowTemplateDropdown(props: any) {
  const { onChange, value, featureTypeId, defaultOpen = false } = props;

  const { gqlClientForProject } = useContext(ProjectContext);
  const { data: projectWorkflowTemplates, loading } = useCIQQuery<{
    project_workflow_templates: Array<any>;
  }>(GET_PROJECT_WORKFLOW_TEMPLATE_NAMES, {
    client: gqlClientForProject,
    variables: { featureId: featureTypeId }
  });

  const dataLoaded = !loading;

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (defaultOpen && dataLoaded) {
      setOpen(true);
    }
  }, [defaultOpen, dataLoaded]);

  const options = useMemo(() => {
    const tooltip = "tooltip";
    if (
      projectWorkflowTemplates &&
      projectWorkflowTemplates.project_workflow_templates
    ) {
      const templates =
        projectWorkflowTemplates.project_workflow_templates.filter(
          (template: any) => !template.disabled || template.id === value
        );

      return templates.map((t) => ({
        label: t.name,
        value: t.id,
        tooltip
      }));
    }
    return [] as any[];
  }, [projectWorkflowTemplates, value]);

  return (
    <Select
      className="w-full"
      options={options}
      value={value}
      onChange={onChange}
      loading={loading}
      placeholder="Select a template"
      data-testid="dropdowneditor-select"
      {...(defaultOpen && { open, onOpenChange: setOpen })}
    />
  );
}

export function ResponsibleContractorDropdown(props: any) {
  const {
    onChange,
    value,
    participantsSource = "materialTradePartners",
    className = "w-full",
    defaultOpen = false
  } = props;
  const { projectParticipants } = useProjectParticipants();
  const dataLoaded = projectParticipants?.dataLoaded;
  const subContractors =
    projectParticipants[participantsSource as keyof ProjectParticipants];

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (defaultOpen && dataLoaded) {
      setOpen(true);
    }
  }, [defaultOpen, dataLoaded]);

  return (
    <Select
      className={className}
      allowClear
      showSearch={{ optionFilterProp: "label" }}
      value={value || undefined}
      loading={!dataLoaded}
      options={
        subContractors
          ? subContractors.map((company: any) => ({
              label: company.subscription_vendor.name,
              value: company.vendor_id
            }))
          : []
      }
      onChange={onChange}
      {...(defaultOpen && { open, onOpenChange: setOpen })}
    />
  );
}

type Props = CustomCellEditorProps & {
  user: any;
};

function EntityDropdownCellEditor(props: Props) {
  const { stopEditing, value, user, data, onValueChange } = props;

  const options = useMemo(
    () => ({
      value,
      participantsSource: user.participantsSource,
      onChange: (val: string, option: any) => {
        const res = {} as any;
        setNestedValue(res, user.id, option?.value || null);
        if (option?.firstName) {
          setNestedValue(res, user.firstName, option.firstName);
          setNestedValue(res, user.lastName, option.lastName);
        } else {
          setNestedValue(res, user.firstName, option?.label || "");
          setNestedValue(res, user.lastName, undefined);
        }
        if (user.statusId) {
          setNestedValue(res, user.statusId, undefined);
        }
        if (user.companyObjField && option?.company) {
          setNestedValue(res, user.companyObjField, option.company);
        }
        onValueChange(res);
        setTimeout(() => {
          stopEditing();
        }, 200);
      }
    }),
    [onValueChange, stopEditing, user, value]
  );

  const dropDrop = useMemo(() => {
    switch (user.dropdownType) {
      case "RC_dropdown":
        return (
          <ResponsibleContractorDropdown {...options} defaultOpen={true} />
        );
      case "RDF_dropdown":
        return (
          <ResponsibleDesignFirmsDropdown {...options} defaultOpen={true} />
        );
      case "RD_dropdown": {
        const selectedCompanyFieldId = getValueFromDotPath(
          data,
          user.dependentId
        );
        return (
          <DependedCompanyUserDropdownComponent
            {...options}
            projectParticipantType="designReviewers"
            selectedCompanyFieldId={selectedCompanyFieldId}
            companyNotFoundContent={user.dependentNotFoundContent}
            defaultOpen={true}
          />
        );
      }
      case "RC_ASSIGNEE_dropdown": {
        const selectedCompanyFieldId = getValueFromDotPath(
          data,
          user.dependentId
        );
        return (
          <DependedCompanyUserDropdownComponent
            {...options}
            projectParticipantType="submitterUsers"
            selectedCompanyFieldId={selectedCompanyFieldId}
            defaultOpen={true}
          />
        );
      }
      case "Template_dropdown":
        return (
          <WorkflowTemplateDropdown
            {...options}
            featureTypeId={user.featureTypeId}
            defaultOpen={true}
          />
        );
      case "DR_dropdown":
        return <DesignReviewerDropdown {...options} defaultOpen={true} />;
      default:
        return <GCReviewersDropdown {...options} defaultOpen={true} />;
    }
  }, [data, options, user]);

  return <div className="w-full !h-full flex items-center">{dropDrop}</div>;
}

export default EntityDropdownCellEditor;
